// The arithmetic that keeps a Training Overview table parked where the reader left it: how far a
// table is scrolled from its right end, the scroll offset that puts it back there, and the width of
// the empty spacer column that makes the sessions in view whole on both sides.
//
// It lives apart from the component because none of it needs Angular or the DOM. Plain functions
// can be tested by importing this file, where the component cannot be booted by the client's unit
// tests at all. The component measures the elements, calls these, and writes the answers back.

/** what the component measures on one table's scrolling box, with the spacer column set to 0 wide */
export interface OverviewTableMeasurements {
  /** the box's visible width (`clientWidth`) */
  boxWidth: number;
  /** the width of the fixed columns on the left, which the stylesheet gives the box as its scroll padding */
  fixedWidth: number;
  /** where the last session column ends: the spacer column's left edge */
  contentEnd: number;
  /** each session column's left edge, oldest (leftmost) first */
  sessionStarts: number[];
}

/** how far a box is scrolled from its right end; the newest session is on the right, so 0 shows it */
export function distanceFromRight(scrollWidth: number, boxWidth: number, scrollLeft: number): number {
  return scrollWidth - boxWidth - scrollLeft;
}

/** the scroll offset that puts a box the given distance from its right end, never before its left end */
export function scrollLeftFor(scrollWidth: number, boxWidth: number, distance: number): number {
  return Math.max(0, scrollWidth - boxWidth - distance);
}

/**
 * The spacer column's width, in pixels, for a table scrolled to its right end.
 *
 * The box rarely holds a whole number of session columns, so the spacer takes the leftover width:
 * the leftmost session that still fits then starts exactly at the edge of the fixed columns, and the
 * newest ends inside the box. A table that fits the box whole does not scroll and needs no spacer.
 * When not even one session fits beside the fixed columns, the newest one is the one lined up.
 */
export function spacerWidth(measurements: OverviewTableMeasurements): number {
  const { boxWidth, fixedWidth, contentEnd, sessionStarts } = measurements;
  if (sessionStarts.length === 0 || contentEnd <= boxWidth) return 0;
  const visibleWidth = boxWidth - fixedWidth;
  const firstThatFits = sessionStarts.find(start => contentEnd - start <= visibleWidth) ?? sessionStarts[sessionStarts.length - 1];
  return Math.max(0, visibleWidth - (contentEnd - firstThatFits));
}
